import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  CancelOutlined as CancelOutlinedIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import AdminDataDeletionTab from '../../components/common/AdminDataDeletionTab';
import apiService from '../../services/api';
import { User, Subscription } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

const PatientsPage: React.FC = () => {
  const { hasAnyPermission } = useAuth();
  const canViewSubscriptions = hasAnyPermission(['admin.subscriptions.view', 'admin.subscriptions.manage']);

  const [patients, setPatients] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [accountStatusFilter, setAccountStatusFilter] = useState<string>('all');
  const [selectedPatient, setSelectedPatient] = useState<User | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [statistics, setStatistics] = useState<any>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [dialogTab, setDialogTab] = useState(0);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [patientSubscriptions, setPatientSubscriptions] = useState<Record<number, Subscription | null>>({});
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 100,
    total: 0,
  });

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');

      // Build filter parameters
      const params: any = {
        user_type: 'patient',
        per_page: pagination.pageSize,
        page: pagination.page + 1,
        search: searchQuery || undefined,
      };

      // Verification filter removed for now

      // Add account status filter if not 'all'
      if (accountStatusFilter !== 'all') {
        params.is_active = accountStatusFilter === 'active';
      }

      const response = await apiService.getUsers(params);
      const patientsList = response.data || [];
      setPatients(patientsList);
      setPagination(prev => ({ ...prev, total: response.pagination?.total || 0 }));
      
      // Fetch subscription status only if role has subscription permissions
      if (canViewSubscriptions) {
      const subscriptionMap: Record<number, Subscription | null> = {};
      await Promise.all(
        patientsList.map(async (patient: User) => {
          try {
            const subResponse = await apiService.getAllSubscriptions({ 
              user_id: patient.id, 
              status: 'active',
              per_page: 1 
            });
            const activeSubscription = subResponse.data.find(
              (sub: Subscription) => sub.status === 'active' && new Date(sub.end_date) > new Date()
            ) || null;
            subscriptionMap[patient.id] = activeSubscription;
            } catch (_err) {
            subscriptionMap[patient.id] = null;
          }
        })
      );
      setPatientSubscriptions(subscriptionMap);
      } else {
        setPatientSubscriptions({});
      }
    } catch (err: any) {
      console.error('Error fetching patients:', err);
      
      // Handle rate limit errors with user-friendly message
      if (err.status === 429 || err.response?.status === 429) {
        const retryAfter = err.retryAfter || err.response?.headers?.['retry-after'] || 60;
        setError(`Too many requests. Please wait ${retryAfter} seconds before trying again.`);
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to load patients');
      }
      setPatients([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, accountStatusFilter, canViewSubscriptions, pagination.page, pagination.pageSize]);

  // Fetch statistics only on mount
  useEffect(() => {
    fetchStatistics();
  }, []);

  // Reset to the first page whenever the search query or filters change,
  // so a stale page index from a previous, larger result set can't produce an empty grid.
  useEffect(() => {
    setPagination(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [searchQuery, accountStatusFilter]);

  // Fetch data on mount and whenever search/filters/pagination change (debounced so
  // typing in the search box doesn't fire a request per keystroke)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData();
    }, 500);
    return () => clearTimeout(timer);
  }, [fetchData]);

  const fetchStatistics = async () => {
    try {
      const stats = await apiService.getUserStatistics();
      setStatistics(stats);
    } catch (err: any) {
      console.error('Error fetching statistics:', err);
      // Don't show error for rate limit - it will be handled by the API interceptor
      // Only set error state for other errors if needed
      if (err.status !== 429) {
        // Handle other errors silently or show a less intrusive message
      }
    }
  };

  const loadSubscriptions = useCallback(async (userId: number) => {
    if (!canViewSubscriptions) {
      setSubscriptions([]);
      return;
    }
    try {
      setSubscriptionsLoading(true);
      // Get subscriptions for this specific user
      const response = await apiService.getAllSubscriptions({ user_id: userId, per_page: 100 });
      setSubscriptions(response.data);
    } catch (err: any) {
      console.error('Error loading subscriptions:', err);
      setSubscriptions([]);
    } finally {
      setSubscriptionsLoading(false);
    }
  }, [canViewSubscriptions]);

  const handleViewDetails = useCallback(async (patient: User) => {
    setSelectedPatient(patient);
    setOpenDialog(true);
    setDialogTab(0);
    // Load subscriptions when dialog opens
    if (patient.id) {
      loadSubscriptions(patient.id);
    }
  }, [loadSubscriptions]);

  // If we navigated here from Leads, auto-open the user detail dialog.
  // Fetched directly by id rather than looked up in `patients`, since with real
  // server-side pagination the target user may not be on the currently loaded page.
  useEffect(() => {
    const type = sessionStorage.getItem('admin_open_user_type');
    const idRaw = sessionStorage.getItem('admin_open_user_id');
    if (type !== 'patient' || !idRaw) return;

    const id = Number(idRaw);
    if (!Number.isFinite(id)) return;

    sessionStorage.removeItem('admin_open_user_type');
    sessionStorage.removeItem('admin_open_user_id');

    apiService.getUser(id)
      .then((user) => handleViewDetails(user))
      .catch((err) => console.error('Error opening patient from lead:', err));
  }, [handleViewDetails]);

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
    setSelectedPatient(null);
    setDialogTab(0);
    setSubscriptions([]);
  }, []);

  const handlePatientDeleted = useCallback(() => {
    handleCloseDialog();
    fetchData();
  }, [fetchData, handleCloseDialog]);

  const handleActiveToggle = async (patient: User) => {
    try {
      setActionLoading(patient.id);
      const updated = await apiService.updateUserActiveStatus(patient.id, !patient.is_active);
      // Update the patient in the list
      setPatients(prev => prev.map(p => p.id === patient.id ? updated : p));
      // Update selected patient if dialog is open
      if (selectedPatient?.id === patient.id) {
        setSelectedPatient(updated);
      }
      // Refresh statistics
      fetchStatistics();
    } catch (err: any) {
      console.error('Error updating active status:', err);
      alert(err.response?.data?.message || 'Failed to update active status');
    } finally {
      setActionLoading(null);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon fontSize="small" color="action" />
          <Typography variant="body2">{params.row.name}</Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailIcon fontSize="small" color="action" />
          <Typography variant="body2">{params.row.email}</Typography>
        </Box>
      ),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PhoneIcon fontSize="small" color="action" />
          <Typography variant="body2">{params.row.phone || 'N/A'}</Typography>
        </Box>
      ),
    },
    ...(canViewSubscriptions ? [{
      field: 'subscription_status',
      headerName: 'Subscription',
      width: 160,
      renderCell: (params: { row: User }) => {
        const subscription = patientSubscriptions[params.row.id];
        const hasActiveSubscription = subscription && 
          subscription.status === 'active' && 
          new Date(subscription.end_date) > new Date();
        
        if (hasActiveSubscription && subscription) {
          const endDate = new Date(subscription.end_date);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <CheckCircleOutlineIcon 
                sx={{ 
                  color: 'success.main', 
                  fontSize: '18px' 
                }} 
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.1 }}>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'success.main' }}>
                  Subscribed
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}>
                  Until: {endDate.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </Typography>
              </Box>
            </Box>
          );
        }
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <CancelOutlinedIcon 
              sx={{ 
                color: 'error.main', 
                fontSize: '18px' 
              }} 
            />
            <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'error.main' }}>
              Un-Subscribed
            </Typography>
          </Box>
        );
      },
    }] : []),
    {
      field: 'created_at',
      headerName: 'Joined',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {new Date(params.row.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
        </Typography>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: (params) => {
        const actions = [
          <GridActionsCellItem
            key="view"
            icon={<VisibilityIcon />}
            label="View Details"
            onClick={() => handleViewDetails(params.row)}
            showInMenu={false}
          />,
        ];

        // Add activate/deactivate action
        actions.push(
          <GridActionsCellItem
            key="toggle"
            icon={params.row.is_active ? <BlockIcon /> : <CheckCircleIcon />}
            label={params.row.is_active ? 'Deactivate' : 'Activate'}
            onClick={() => handleActiveToggle(params.row)}
            disabled={actionLoading === params.row.id}
            showInMenu={false}
          />
        );

        return actions;
      },
    },
  ];

  // Active/Subscribed are derived from the currently loaded page only (not the full
  // patient base) since the backend has no dedicated aggregate endpoint for them.
  const patientStats = {
    total: pagination.total || patients.length,
    active: patients.filter(p => p.is_active).length,
    subscribed: Object.values(patientSubscriptions).filter(sub =>
      sub && sub.status === 'active' && new Date(sub.end_date) > new Date()
    ).length,
  };

  const dataDeletionTabIndex = canViewSubscriptions ? 2 : 1;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Patient Management
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {statistics?.by_user_type?.patient || patientStats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Patients
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="info.main">
                {patientStats.active}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                {patientStats.subscribed}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Subscribed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            placeholder="Search patients by name, email, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
              />
            </Grid>
            {/* Verification Status filter hidden for now */}
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Account Status</InputLabel>
                <Select
                  value={accountStatusFilter}
                  label="Account Status"
                  onChange={(e) => setAccountStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={patients}
              columns={columns}
              paginationMode="server"
              paginationModel={{ page: pagination.page, pageSize: pagination.pageSize }}
              onPaginationModelChange={(model) => setPagination(prev => ({ ...prev, page: model.page, pageSize: model.pageSize }))}
              pageSizeOptions={[10, 25, 50, 100]}
              rowCount={pagination.total}
              disableRowSelectionOnClick
              autoHeight
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Patient Details</Typography>
            {selectedPatient && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                {/* Verification status hidden for now */}
                {selectedPatient.is_active ? (
                  <Chip label="Active" color="success" size="small" />
                ) : (
                  <Chip label="Inactive" color="error" size="small" />
                )}
              </Box>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPatient && (
            <Box sx={{ mt: 2 }}>
            <Tabs value={dialogTab} onChange={(e, newValue) => setDialogTab(newValue)} sx={{ mb: 3 }}>
              <Tab label="Overview" />
              {canViewSubscriptions && <Tab label="Subscription" />}
              <Tab label="Data Deletion" />
            </Tabs>

              {/* Overview Tab */}
              {dialogTab === 0 && (
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Name
                    </Typography>
                    <Typography variant="body1">{selectedPatient.name}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1">{selectedPatient.email}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography variant="body1">{selectedPatient.phone || 'N/A'}</Typography>
                  </Grid>
                  {selectedPatient.profile && (
                    <>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          City
                        </Typography>
                        <Typography variant="body1">
                          {selectedPatient.profile.city || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          State
                        </Typography>
                        <Typography variant="body1">
                          {selectedPatient.profile.state || 'N/A'}
                        </Typography>
                      </Grid>
                    </>
                  )}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Signup Method
                    </Typography>
                    <Typography variant="body1">
                      {selectedPatient.signup_method || 'email'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Joined Date
                    </Typography>
                    <Typography variant="body1">
                      {new Date(selectedPatient.created_at).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Grid>
                </Grid>
              )}

              {/* Subscription Tab */}
              {dialogTab === 1 && canViewSubscriptions && (
                <Box>
                  {subscriptionsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : subscriptions.length === 0 ? (
                    <Alert severity="info">
                      This patient has no active subscriptions.
                    </Alert>
                  ) : (
                    <Box>
                      {subscriptions.map((subscription) => (
                        <Card key={subscription.id} sx={{ mb: 2 }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                              <Box>
                                <Typography variant="h6" gutterBottom>
                                  {subscription.subscription_plan?.name || 'Connect Plan'}
                                </Typography>
                                <Chip
                                  label={subscription.status.toUpperCase()}
                                  color={
                                    subscription.status === 'active'
                                      ? 'success'
                                      : subscription.status === 'expired'
                                      ? 'warning'
                                      : subscription.status === 'cancelled'
                                      ? 'error'
                                      : 'default'
                                  }
                                  size="small"
                                  sx={{ mb: 1 }}
                                />
                              </Box>
                              <Typography variant="h6" color="primary">
                                ₹{subscription.amount_paid}
                              </Typography>
                            </Box>
                            <Grid container spacing={2}>
                              <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                  Subscription Reference
                                </Typography>
                                <Typography variant="body2">{subscription.subscription_reference}</Typography>
                              </Grid>
                              <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                  Type
                                </Typography>
                                <Typography variant="body2">
                                  {subscription.subscription_type === 'connect_plan' ? 'Connect Plan' : 'Pro Membership'}
                                </Typography>
                              </Grid>
                              <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                  Start Date
                                </Typography>
                                <Typography variant="body2">
                                  {new Date(subscription.start_date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                </Typography>
                              </Grid>
                              <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                  End Date
                                </Typography>
                                <Typography variant="body2">
                                  {new Date(subscription.end_date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                </Typography>
                              </Grid>
                              <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                  Credits Limit
                                </Typography>
                                <Typography variant="body2">
                                  {subscription.credits_limit === null || subscription.credits_limit === undefined ? 'Unlimited' : subscription.credits_limit}
                                </Typography>
                              </Grid>
                              <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                  Credits Used
                                </Typography>
                                <Typography variant="body2">
                                  {subscription.credits_used} / {subscription.credits_limit === null || subscription.credits_limit === undefined ? '∞' : subscription.credits_limit}
                                </Typography>
                              </Grid>
                              <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                  Payment Method
                                </Typography>
                                <Typography variant="body2">{subscription.payment_method || 'N/A'}</Typography>
                              </Grid>
                              <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                  Auto Renew
                                </Typography>
                                <Typography variant="body2">
                                  {subscription.auto_renew ? 'Yes' : 'No'}
                                </Typography>
                              </Grid>
                              {subscription.cancelled_at && (
                                <Grid size={{ xs: 12 }}>
                                  <Alert severity="warning">
                                    Cancelled on {new Date(subscription.cancelled_at).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                    {subscription.cancellation_reason && (
                                      <Typography variant="body2" sx={{ mt: 1 }}>
                                        Reason: {subscription.cancellation_reason}
                                      </Typography>
                                    )}
                                  </Alert>
                                </Grid>
                              )}
                            </Grid>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {dialogTab === dataDeletionTabIndex && selectedPatient && (
                <AdminDataDeletionTab user={selectedPatient} onDeleted={handlePatientDeleted} />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedPatient && (
            <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {/* Verify/Unverify buttons hidden for now */}
                <Button
                  variant={selectedPatient.is_active ? "outlined" : "contained"}
                  color={selectedPatient.is_active ? "error" : "success"}
                  startIcon={selectedPatient.is_active ? <BlockIcon /> : <CheckCircleIcon />}
                  onClick={() => handleActiveToggle(selectedPatient)}
                  disabled={actionLoading === selectedPatient.id}
                >
                  {selectedPatient.is_active ? 'Deactivate' : 'Activate'}
                </Button>
              </Box>
              <Button onClick={handleCloseDialog}>Close</Button>
            </Box>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientsPage;

